<template>
  <v-row>
    <v-col cols="12" md="4">
      <v-card>
        <v-card-title class="d-flex align-center">
          <span>Szállás igény részletei</span>
          <v-spacer />
          <v-tooltip v-if="!canEdit" location="bottom">
            <template #activator="{ props: tooltipProps }">
              <span v-bind="tooltipProps">
                <v-btn
                  color="primary"
                  variant="text"
                  size="small"
                  prepend-icon="mdi-pencil"
                  :disabled="true"
                >
                  Módosítás
                </v-btn>
              </span>
            </template>
            <span>{{ editBlockedReason }}</span>
          </v-tooltip>
          <v-btn
            v-else
            color="primary"
            variant="text"
            size="small"
            prepend-icon="mdi-pencil"
            @click="$emit('editInquiry')"
          >
            Módosítás
          </v-btn>
        </v-card-title>
        <v-container>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="
                  formatPublicReference(
                    props.accommodationInquiry.publicReference
                  )
                "
                label="Referencia"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="props.accommodationInquiry.costCenterName"
                label="Költséghely"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="4">
              <v-text-field
                v-model="props.accommodationInquiry.area"
                label="Helység"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="props.accommodationInquiry.numberOfPeople"
                label="Létszám"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-select
                label="Státusz"
                variant="outlined"
                v-model="props.accommodationInquiry.status"
                :items="inquiryStatusOptions"
                item-title="label"
                item-value="value"
                @update:modelValue="handleStatusChange"
              >
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="props.accommodationInquiry.from"
                label="Érkezés"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="props.accommodationInquiry.to"
                label="Távozás"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                :model-value="
                  placementTypeLabel(props.accommodationInquiry.placementType)
                "
                label="Elhelyezés"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="props.accommodationInquiry.contactName"
                label="Kapcsolattartó neve"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="props.accommodationInquiry.contactPhone"
                label="Kapcsolattartó telefonszáma"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="props.accommodationInquiry.catering"
                label="Ellátás"
                variant="outlined"
                rows="2"
                readonly
                hide-details
              ></v-textarea>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="props.accommodationInquiry.comment"
                label="Komment"
                variant="outlined"
                rows="2"
                readonly
                hide-details
              ></v-textarea>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-col>
    <v-spacer></v-spacer>
    <v-col cols="12" md="8">
      <v-card class="offers-card">
        <v-card-title> Ajánlatok </v-card-title>

        <v-alert
          v-if="draftOffers.length > 0"
          type="warning"
          variant="tonal"
          class="ma-4 draft-summary-alert"
          border="start"
          prominent
        >
          <div class="d-flex align-center flex-wrap" style="gap: 12px">
            <div class="flex-grow-1">
              <div class="text-subtitle-1 font-weight-bold">
                ⚠ {{ draftOffers.length }} olyan ajánlat van, amit az ügyfél MÉG
                NEM LÁT
              </div>
              <div class="text-caption">
                A piszkozatok kiválasztásával vagy az "Összes elküldése" gombbal
                teheti őket láthatóvá az ügyfél számára.
              </div>
            </div>
            <v-btn
              color="warning"
              variant="flat"
              :disabled="selectedDraftIds.size === 0 || publishing"
              :loading="publishing"
              @click="openBulkPublishConfirm('selected')"
            >
              Kiválasztottak elküldése ({{ selectedDraftIds.size }})
            </v-btn>
            <v-btn
              color="warning"
              variant="outlined"
              :disabled="publishing"
              :loading="publishing"
              @click="openBulkPublishConfirm('all')"
            >
              Összes elküldése
            </v-btn>
          </div>
        </v-alert>

        <v-container v-if="props.accommodationInquiry.offerEntities.length > 0">
          <div
            v-for="offer in props.accommodationInquiry.offerEntities"
            :key="offer.id"
          >
            <AdminOfferTable
              :offer="offer"
              :can-accept="!hasAcceptedOffer && offer.status !== 'DRAFT'"
              :selected="selectedDraftIds.has(offer.id)"
              @update:selected="(v) => toggleDraftSelection(offer.id, v)"
              @accept-offer="acceptOffer"
              @publish-offer="publishSingleOffer"
              @edit-offer="editDraftOffer"
              @delete-offer="deleteDraftOffer"
            ></AdminOfferTable>
          </div>
        </v-container>
        <v-container v-else> Még nincsenek ajánlatok </v-container>
        <!-- ACTIONS -->
        <v-card-actions class="pa-4">
          <!-- Secondary action -->
          <CreateOffer
            v-if="isAdmin"
            ref="createOfferRef"
            :inquiryId="inquiryId"
            :inquiry="props.accommodationInquiry"
            :from="props.accommodationInquiry.from"
            :to="props.accommodationInquiry.to"
            :numberOfPeople="props.accommodationInquiry.numberOfPeople"
            @offer-created="onOfferCreated"
          />

          <v-spacer />

          <!-- Primary action -->
          <CreateBooking
            v-if="!hasBooking"
            :accommodationInquiry="props.accommodationInquiry"
          />

          <v-btn
            v-else
            color="primary"
            :to="`/admin/bookings/${props.accommodationInquiry.publicReference}`"
          >
            Tovább a foglaláshoz
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-col>
  </v-row>

  <v-dialog v-model="showBulkPublishConfirm" max-width="520">
    <v-card>
      <v-card-title>Ajánlatok küldése az ügyfélnek</v-card-title>
      <v-card-text>
        <p class="mb-2">
          Az alábbi {{ pendingPublishOffers.length }} ajánlat ezzel láthatóvá
          válik az ügyfél számára:
        </p>
        <ul class="pl-4">
          <li
            v-for="offer in pendingPublishOffers"
            :key="offer.id"
            class="text-body-2"
          >
            {{ offer.items[0]?.accommodation || "—" }} ({{ offer.items.length }}
            tétel)
          </li>
        </ul>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text color="grey" @click="showBulkPublishConfirm = false"
          >Mégse</v-btn
        >
        <v-btn color="warning" variant="flat" @click="confirmBulkPublish"
          >Igen, küldés</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script lang="ts">
import { defineComponent, PropType, computed, reactive, ref } from "vue";
import AdminOfferTable from "@/components/AccommodationInquiries/admin/offer/AdminOfferTable.vue";
import CreateOffer from "@/components/AccommodationInquiries/admin/offer/CreateOffer.vue";
import { useAuthStore } from "@/store/AuthStore";
import router from "@/router";
import CreateBooking from "@/components/Bookings/admin/CreateBooking.vue";
import { OfferService } from "@/services/OfferService";
import { useToast } from "@/services/useToast";
import { AccommodationInquiriesService } from "@/services/AccommodationInquiriesService";
import {
  placementTypeLabel,
  isTerminalInquiryStatus,
} from "@/utils/statusLabels";

export default defineComponent({
  components: { CreateBooking, AdminOfferTable, CreateOffer },
  props: {
    accommodationInquiry: {
      type: Object as PropType<any>,
      required: true,
    },
  },
  emits: ["offerAccepted", "offer-created", "editInquiry"],
  setup(props, ctx) {
    const offerService = new OfferService();
    const accommodationInquiriesService = new AccommodationInquiriesService(
      "/admin/inquiry"
    );
    const authStore = useAuthStore();
    const isAdmin = authStore.isAdmin;
    const inquiryId: string = router.currentRoute.value.params.id as string;
    const { showToast } = useToast();

    const hasBooking = computed(
      () => props.accommodationInquiry.hasBooking == true
    );

    const hasAcceptedOffer = computed(() =>
      props.accommodationInquiry.offerEntities?.some(
        (offer: any) => offer.status === "ACCEPTED"
      )
    );
    /** Hides what the server would refuse: an accepted offer or a closed inquiry ends editing. */
    const canEdit = computed(
      () =>
        !hasAcceptedOffer.value &&
        !isTerminalInquiryStatus(props.accommodationInquiry.status)
    );
    /** Why the button is greyed out. A disabled control that cannot say why is a dead end. */
    const editBlockedReason = computed(() =>
      hasAcceptedOffer.value
        ? "Elfogadott ajánlat után az igény már nem módosítható"
        : "A lezárt igény már nem módosítható"
    );

    const draftOffers = computed(() =>
      (props.accommodationInquiry.offerEntities ?? []).filter(
        (o: any) => o.status === "DRAFT"
      )
    );

    const selectedDraftIds = reactive(new Set<number>());
    const toggleDraftSelection = (offerId: number, selected: boolean) => {
      if (selected) selectedDraftIds.add(offerId);
      else selectedDraftIds.delete(offerId);
    };

    const showBulkPublishConfirm = ref(false);
    const pendingPublishOffers = ref<any[]>([]);
    const publishing = ref(false);

    const openBulkPublishConfirm = (mode: "selected" | "all") => {
      const targets =
        mode === "all"
          ? draftOffers.value
          : draftOffers.value.filter((o: any) => selectedDraftIds.has(o.id));
      if (targets.length === 0) return;
      pendingPublishOffers.value = targets;
      showBulkPublishConfirm.value = true;
    };

    const confirmBulkPublish = async () => {
      if (publishing.value) return;
      publishing.value = true;
      try {
        const ids = pendingPublishOffers.value.map((o: any) => o.id);
        await offerService.publishOffers(inquiryId, ids);
        ids.forEach((id: number) => selectedDraftIds.delete(id));
        showBulkPublishConfirm.value = false;
        pendingPublishOffers.value = [];
        showToast(`${ids.length} ajánlat elküldve az ügyfélnek`);
        ctx.emit("offer-created");
      } catch {
        showToast("Az ajánlatok küldése sikertelen", "error");
      } finally {
        publishing.value = false;
      }
    };

    const publishSingleOffer = async (offerId: number) => {
      try {
        await offerService.publishOffers(inquiryId, [offerId]);
        selectedDraftIds.delete(offerId);
        showToast("Ajánlat elküldve az ügyfélnek");
        ctx.emit("offer-created");
      } catch {
        showToast("Az ajánlat küldése sikertelen", "error");
      }
    };

    const createOfferRef = ref<any>(null);

    /** Reopens the draft in the offer dialog, which doubles as the editor. */
    const editDraftOffer = (offer: any) => {
      createOfferRef.value?.openForEdit(offer);
    };

    const deleteDraftOffer = async (offerId: number) => {
      try {
        await offerService.deleteDraftOffer(offerId);
        selectedDraftIds.delete(offerId);
        showToast("Piszkozat törölve");
        ctx.emit("offer-created");
      } catch {
        showToast("A piszkozat törlése sikertelen", "error");
      }
    };

    /**
     * The dropdown used to change nothing but the screen: the status endpoint existed, nothing
     * called it, and a reload put the old value back. It also decides whether the inquiry can
     * still be edited, so it has to reach the server.
     */
    async function handleStatusChange(status: string) {
      try {
        await accommodationInquiriesService.setStatus(
          Number(inquiryId),
          status
        );
        showToast("Státusz módosítva");
        ctx.emit("offer-created");
      } catch (error) {
        showToast("A státusz módosítása sikertelen", "error");
        ctx.emit("offer-created");
      }
    }

    function onOfferCreated() {
      ctx.emit("offer-created");
    }

    const acceptOffer = async (offerId: number) => {
      try {
        await offerService.adminAcceptOffer(offerId);
        ctx.emit("offerAccepted", offerId);
      } catch (error) {
        console.error(error);
      }
    };
    function formatPublicReference(ref: string) {
      if (!ref) return "";
      return ref.match(/.{1,4}/g)!.join("-");
    }

    return {
      props,
      handleStatusChange,
      canEdit,
      editBlockedReason,
      isAdmin,
      inquiryId,
      onOfferCreated,
      formatPublicReference,
      hasBooking,
      acceptOffer,
      hasAcceptedOffer,
      draftOffers,
      selectedDraftIds,
      toggleDraftSelection,
      showBulkPublishConfirm,
      pendingPublishOffers,
      publishing,
      openBulkPublishConfirm,
      confirmBulkPublish,
      publishSingleOffer,
      createOfferRef,
      editDraftOffer,
      deleteDraftOffer,
      placementTypeLabel,
      inquiryStatusOptions: [
        { label: "Új", value: "NEW" },
        { label: "Folyamatban", value: "PENDING" },
        { label: "Ajánlat elküldve", value: "OFFER SENT" },
        { label: "Ajánlat elfogadva", value: "OFFER ACCEPTED" },
        { label: "Teljesítve", value: "COMPLETED" },
        { label: "Lefoglalva", value: "BOOKED" },
        { label: "Törölve", value: "CANCELLED" },
      ],
    };
  },
});
</script>
<style scoped>
.draft-summary-alert {
  border-left-width: 6px !important;
}
</style>
